import jwtDecode from 'jwt-decode';
import isJwtLikeToken from './isJwtLikeToken';

// Only JWTs carry an expiry; legacy short addon tokens are treated as not expired.
function isExpiredJwt(token) {
	if (!isJwtLikeToken(token)) {
		return false;
	}

	try {
		const decoded = jwtDecode(token);
		return !!decoded && typeof decoded.exp === 'number' && decoded.exp * 1000 <= Date.now();
	} catch (e) {
		return true;
	}
}

/**
 * Decide which token (if any) may be injected as Token._sessionToken on /channel-granted.
 *
 * Provider callback query params (e.g. Dotykacka API `token`) must not override a real
 * Dativery session. Only tokens we intentionally bridged (sessionStorage / OAuth state)
 * or JWT-like query tokens are eligible, and only the sessionStorage bridge may override
 * a valid stored session (URL-borne values can be crafted by a third party).
 *
 * @param {object} args
 * @param {string|null|undefined} args.stateToken - token from decoded OAuth state
 * @param {string|null|undefined} args.queryToken - URL ?token=
 * @param {string|null|undefined} args.queryAuthToken - URL ?authToken=
 * @param {string|null|undefined} args.sessionStorageToken - consumeChannelGrantToken()
 * @param {boolean} args.hasValidStoredSession - true when localStorage JWT is still valid
 * @returns {string|null}
 */
export function resolveChannelGrantSessionToken({
	stateToken,
	queryToken,
	queryAuthToken,
	sessionStorageToken,
	hasValidStoredSession,
}) {
	// The sessionStorage bridge is written only by our own callGrant in this tab, so it wins even over a
	// valid stored session (e.g. FlexiBee authToken user while the browser is logged in as someone else).
	// An expired bridge (e.g. left over from an abandoned grant) would only force a login, so fall through.
	if (sessionStorageToken && !isExpiredJwt(sessionStorageToken)) {
		return sessionStorageToken;
	}

	if (hasValidStoredSession) {
		return null;
	}

	if (stateToken) {
		return stateToken;
	}

	if (isJwtLikeToken(queryToken)) {
		return queryToken;
	}

	if (isJwtLikeToken(queryAuthToken)) {
		return queryAuthToken;
	}

	return null;
}
